<template>
  <div class="mt-4">
    <div v-if="log.photo_media_uuid" class="d-flex flex-wrap gap-2 mb-4">
      <button
        class="btn btn-outline-violet text-uppercase"
        :disabled="isGeneratingQr || isRemovingPhoto"
        @click="$emit('replace-photo')"
      >
        {{ $t("Replace photo") }}
      </button>
      <button
        class="btn btn-outline-danger text-uppercase"
        :disabled="isGeneratingQr || isRemovingPhoto"
        @click="$emit('remove-photo')"
      >
        {{ isRemovingPhoto ? $t("Removing...") : $t("Remove photo") }}
      </button>
    </div>

    <div v-if="showPhotoEditor" class="mb-4">
      <h3 class="text-violet text-uppercase h5 mb-3">
        {{ log.photo_media_uuid ? $t("Replace photo") : $t("Attach photo") }}
      </h3>
      <div
        class="d-flex align-items-center justify-content-between border rounded-custom px-3 py-2 mb-3"
      >
        <span
          class="small fw-semibold text-uppercase"
          :class="!useDesktopUploadInstead ? 'text-violet' : 'text-muted'"
        >
          {{ $t("Smartphone") }}
        </span>
        <div class="form-check form-switch mb-0">
          <input
            id="cam-upload-mode"
            v-model="localDesktopMode"
            class="form-check-input"
            type="checkbox"
          />
        </div>
        <span
          class="small fw-semibold text-uppercase"
          :class="localDesktopMode ? 'text-violet' : 'text-muted'"
        >
          {{ $t("Computer") }}
        </span>
      </div>

      <template v-if="!localDesktopMode">
        <p class="text-muted small mb-3">
          {{ $t("Scan the QR code with the department smartphone. If you prefer, you can also upload the photo directly from this computer.") }}
        </p>

        <CamProductionQrHelper
          v-if="photoUploadUrl"
          :qr-url="photoUploadUrl"
          :expires-at="photoUploadExpiresAt"
          :photo-attached="Boolean(log?.photo_media_uuid)"
        />

        <button
          class="btn btn-outline-violet text-uppercase w-100 mt-3"
          :disabled="isGeneratingQr"
          @click="$emit('generate-photo-qr')"
        >
          {{ isGeneratingQr ? $t("Preparing link...") : $t("Regenerate photo link") }}
        </button>
      </template>

      <DesktopUploader
        v-else
        :photo-upload-token="photoUploadToken"
        :disabled="isGeneratingQr || isRemovingPhoto"
        @request-token="$emit('generate-photo-qr')"
        @uploaded="$emit('desktop-uploaded', $event)"
        @error="$emit('desktop-error', $event)"
      />
    </div>
  </div>
</template>

<script>
import { computed, watch } from "vue";
import CamProductionQrHelper from "@/components/cam/CamProductionQrHelper.vue";
import DesktopUploader from "@/components/cam/production/DesktopUploader.vue";

export default {
  name: "CamProductionPhotoManager",
  components: {
    CamProductionQrHelper,
    DesktopUploader,
  },
  props: {
    log: {
      type: Object,
      required: true,
    },
    showPhotoEditor: {
      type: Boolean,
      default: false,
    },
    useDesktopUploadInstead: {
      type: Boolean,
      default: false,
    },
    photoUploadToken: {
      type: String,
      default: "",
    },
    photoUploadUrl: {
      type: String,
      default: "",
    },
    photoUploadExpiresAt: {
      type: String,
      default: null,
    },
    isGeneratingQr: {
      type: Boolean,
      default: false,
    },
    isRemovingPhoto: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:useDesktopUploadInstead",
    "generate-photo-qr",
    "replace-photo",
    "remove-photo",
    "desktop-uploaded",
    "desktop-error",
  ],
  setup(props, { emit }) {
    const localDesktopMode = computed({
      get: () => props.useDesktopUploadInstead,
      set: (value) => emit("update:useDesktopUploadInstead", value),
    });

    watch(
      () => [props.useDesktopUploadInstead, props.photoUploadToken, props.log?.uuid],
      ([isDesktopMode, photoUploadToken, logUuid]) => {
        if (isDesktopMode && !photoUploadToken && logUuid) {
          emit("generate-photo-qr");
        }
      },
      { immediate: true },
    );

    return {
      localDesktopMode,
    };
  },
};
</script>
